import { useEffect, useState } from "react";
import { Tabs } from "../primitives/Tabs";
import { getAmbassadorDashboard, type AmbassadorDashboard } from "../../api/admin";
import { AmbassadorDetailsTab } from "./ambassador/AmbassadorDetailsTab";
import { AmbassadorClientsTab } from "./ambassador/AmbassadorClientsTab";
import { AmbassadorPurchasesTab } from "./ambassador/AmbassadorPurchasesTab";
import { AmbassadorManagementTab } from "./ambassador/AmbassadorManagementTab";

type AmbassadorDashboardProps = {
  ambassadorId: string;
  onManageAccess?: () => void;
};

export function AmbassadorDashboard({ ambassadorId, onManageAccess }: AmbassadorDashboardProps) {
  const [dashboard, setDashboard] = useState<AmbassadorDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("details");

  useEffect(() => {
    if (!ambassadorId) {
      setLoading(false);
      setError("Aucun ID d'ambassadeur fourni");
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    getAmbassadorDashboard(ambassadorId)
      .then((dashboardData) => {
        if (!mounted) return;
        setDashboard(dashboardData);
      })
      .catch((error) => {
        console.error("Failed to load dashboard:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        setError(errorMessage);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [ambassadorId]);

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="text-sm text-muted">Chargement du dashboard ambassadeur...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-danger bg-danger/10 p-4">
        <div className="text-sm font-medium text-danger">Erreur lors du chargement</div>
        <div className="text-xs text-muted mt-1">{error}</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="text-sm text-muted">Aucune donnée disponible pour cet ambassadeur</div>
      </div>
    );
  }

  const tabs = [
    { id: "details", label: "Détails" },
    { id: "clients", label: "Clients" },
    { id: "purchases", label: "Achat" },
    { id: "management", label: "Gestion" },
  ];

  return (
    <div className="space-y-4">
      <Tabs
        tabs={tabs}
        activeId={activeTab}
        onChange={setActiveTab}
        variant="underline"
      />
      
      {activeTab === "details" && (
        <AmbassadorDetailsTab
          ambassadorId={ambassadorId}
          dashboard={dashboard}
          onManageAccess={onManageAccess}
        />
      )}
      
      {activeTab === "clients" && (
        <AmbassadorClientsTab ambassadorId={ambassadorId} />
      )}
      
      {activeTab === "purchases" && (
        <AmbassadorPurchasesTab ambassadorId={ambassadorId} />
      )}
      
      {activeTab === "management" && (
        <AmbassadorManagementTab ambassadorId={ambassadorId} dashboard={dashboard} />
      )}
    </div>
  );
}
